import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  Alert, AlertDescription, AlertIcon, Box, Button, Center, Flex, HStack, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter,
  ModalHeader, ModalOverlay, Radio, RadioGroup, SimpleGrid, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, VStack, useToast,
} from '@chakra-ui/react'
import {
  AlertTriangle, ArrowRight, ArrowRightLeft, Ban, Banknote, Building2, Check, CheckCircle2, Clock, FileSearch, Landmark, Navigation, Percent,
  Receipt, Scale, Settings2, ShieldAlert, Truck, Users, Wallet,
} from 'lucide-react'
import { control, type AttentionItem, type RiskTrip, type Tone } from '@services/operators'
import { apiErrorMessage, formatDate, formatNumber, timeAgo } from '@utils/format'
import { ConfirmModal, EmptyState, Money, PageHeader, Panel, Pill, SkeletonRows, StatTile, tableHead } from '@components/ui'
import { useAuthStore } from '@store/authStore'

const TONE_STYLE: Record<Tone, { bar: string; bg: string; icon: string }> = {
  critical: { bar: 'red.400', bg: 'red.50', icon: 'red.600' },
  warning: { bar: 'orange.400', bg: 'orange.50', icon: 'orange.600' },
  info: { bar: 'blue.300', bg: 'blue.50', icon: 'blue.600' },
}

const ICONS: Record<string, React.ElementType> = {
  'settlement-off': Percent, 'no-default-rule': Percent, 'no-key': ShieldAlert, 'trips-at-risk': Navigation, recon: Scale, 'recon-stale': Scale,
  'pending-ops': Building2, 'pending-buses': Truck, 'pending-docs': FileSearch, 'payout-requested': Banknote, 'payout-approved': Banknote,
  accounts: Landmark, lapsed: AlertTriangle, debts: Wallet,
}

const ACTIVITY_LABEL: Record<string, string> = {
  'operator.approve': 'approved a company', 'operator.reject': 'declined a company', 'operator.suspend': 'suspended a company', 'operator.reactivate': 'reactivated a company',
  'operator.signup': 'A company signed up', 'vehicle.approved': 'approved a bus', 'vehicle.rejected': 'rejected a bus', 'vehicle.created': 'A bus was added',
  'vehicle_document.verified': 'verified a document', 'vehicle_document.rejected': 'rejected a document', 'vehicle_document.uploaded': 'A document was uploaded',
  'payout.requested': 'A withdrawal was requested', 'payout.approved': 'approved a withdrawal', 'payout.paid': 'recorded a payment', 'payout.rejected': 'declined a withdrawal',
  'trip.cancelled_by_staff': 'cancelled a trip', 'trip.reassigned': 'moved a trip to another bus', 'ledger.adjustment': 'adjusted a balance', 'ledger.penalty': 'deducted from a balance',
  'settlement.configured': 'changed settlement', 'settlement.run': 'ran settlement', 'reconciliation.gateway': 'checked a ZenoPay statement', 'payout.sheet_exported': 'downloaded the payout sheet',
}

function greeting() {
  const h = new Date().getHours()
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'
}

function AttentionCard({ item }: { item: AttentionItem }) {
  const s = TONE_STYLE[item.tone]
  const Icon = ICONS[item.id] ?? AlertTriangle
  return (
    <Flex
      bg="white" border="1px solid" borderColor="gray.100" borderLeft="4px solid" borderLeftColor={s.bar} borderRadius="xl" p={4} gap={4}
      align={{ base: 'stretch', md: 'center' }} direction={{ base: 'column', md: 'row' }}
      boxShadow="0 1px 2px rgba(16,24,40,.04)" transition="all .15s ease" _hover={{ boxShadow: 'md', borderColor: 'gray.200' }}
    >
      <HStack spacing={4} flex="1" minW={0} align="flex-start">
        <Center w={10} h={10} borderRadius="xl" bg={s.bg} color={s.icon} flexShrink={0}><Icon size={20} /></Center>
        <Box minW={0}>
          <HStack spacing={2} flexWrap="wrap">
            <Text fontWeight="semibold" color="gray.900">{item.title}</Text>
            {item.amount ? <Pill tone={item.tone === 'critical' ? 'red' : 'orange'} dot={false}>TZS {formatNumber(item.amount)}</Pill> : null}
          </HStack>
          <Text fontSize="sm" color="gray.500" mt={0.5}>{item.detail}</Text>
        </Box>
      </HStack>
      <Button as={Link} to={item.href} size="sm" rightIcon={<ArrowRight size={14} />} flexShrink={0} alignSelf={{ base: 'flex-start', md: 'center' }}>{item.cta}</Button>
    </Flex>
  )
}

// ---------------------------------------------------------------------------
// Trips that should not be running
// ---------------------------------------------------------------------------
function MoveTripModal({ trip, onClose, onDone }: { trip: RiskTrip | null; onClose: () => void; onDone: () => void }) {
  const toast = useToast()
  const [bus, setBus] = useState('')
  useEffect(() => { setBus('') }, [trip?.tripId])
  const { data, isLoading } = useQuery(['replacements', trip?.tripId], () => control.replacements(trip!.tripId).then(r => r.data), { enabled: !!trip })
  const move = useMutation(() => control.reassign(trip!.tripId, bus, 'Bus changed by Daladala Smart'), {
    onSuccess: r => { toast({ title: `Trip moved to ${r.data.registrationNumber}. ${r.data.passengersNotified} passenger(s) notified.`, status: 'success' }); onDone(); onClose() },
    onError: e => { toast({ title: apiErrorMessage(e), status: 'error' }) },
  })
  return (
    <Modal isOpen={!!trip} onClose={onClose} isCentered>
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(6px)" />
      <ModalContent borderRadius="2xl">
        <ModalHeader pb={1}>Move this trip to another bus</ModalHeader><ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.600" mb={4}>
            Passengers keep their seat and their fare, and are told about the new bus. Only approved buses that are free at that time are listed.
          </Text>
          {isLoading ? <SkeletonRows rows={3} h={10} /> : !data?.length ? (
            <Alert status="warning" borderRadius="xl" fontSize="sm"><AlertIcon />No other bus is free at that time. You can cancel and refund instead.</Alert>
          ) : (
            <RadioGroup value={bus} onChange={setBus}>
              <VStack align="stretch" spacing={2} maxH="280px" overflowY="auto">
                {data.map(b => (
                  <Box key={b.id} as="label" p={3} border="1px solid" borderColor={bus === b.id ? 'brand.400' : 'gray.200'} bg={bus === b.id ? 'brand.50' : 'white'} borderRadius="xl" cursor="pointer">
                    <Radio value={b.id} colorScheme="brand">
                      <Text fontWeight="semibold" display="inline">{b.registrationNumber}</Text>
                      <Text as="span" fontSize="sm" color="gray.500"> · {b.totalSeats} seats · {b.house ? 'Daladala Smart' : b.operatorName}</Text>
                    </Radio>
                  </Box>
                ))}
              </VStack>
            </RadioGroup>
          )}
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button isDisabled={!bus} isLoading={move.isLoading} onClick={() => move.mutate()}>Move trip</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

function TripsAtRisk({ trips, loading }: { trips: RiskTrip[]; loading: boolean }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [moving, setMoving] = useState<RiskTrip | null>(null)
  const [cancelling, setCancelling] = useState<RiskTrip | 'all' | null>(null)
  const refresh = () => { qc.invalidateQueries('control-trips'); qc.invalidateQueries('control-home') }

  const cancel = useMutation(async ({ list, reason }: { list: RiskTrip[]; reason: string }) => {
    let refunded = 0, bookings = 0, failed = 0
    for (const t of list) {
      try { const r = await control.cancelTrip(t.tripId, reason); refunded += r.data.refundTotal; bookings += r.data.bookingsCancelled } catch { failed++ }
    }
    return { refunded, bookings, failed }
  }, {
    onSuccess: r => {
      toast({ title: `${r.bookings} booking(s) cancelled, TZS ${formatNumber(r.refunded)} refunded${r.failed ? `. ${r.failed} trip(s) could not be cancelled.` : '.'}`, status: r.failed ? 'warning' : 'success', duration: 6000 })
      setCancelling(null); refresh()
    },
    onError: e => { toast({ title: apiErrorMessage(e), status: 'error' }) },
  })

  const totalPax = trips.reduce((s, t) => s + t.passengers, 0)
  const totalRefund = trips.reduce((s, t) => s + t.refund, 0)

  return (
    <Panel
      id="trips" title="Trips that should not be running" flush
      subtitle={`${trips.length} trip${trips.length === 1 ? '' : 's'} · ${totalPax} passenger${totalPax === 1 ? '' : 's'} · TZS ${formatNumber(totalRefund)} to refund`}
      actions={trips.length > 1 ? <Button size="sm" colorScheme="red" variant="outline" leftIcon={<Ban size={14} />} onClick={() => setCancelling('all')}>Cancel all &amp; refund</Button> : undefined}
    >
      {loading ? <SkeletonRows rows={3} /> : trips.length === 0 ? (
        <EmptyState icon={<CheckCircle2 size={26} />} title="Nothing to fix" body="Every upcoming trip is on a bus that is approved and belongs to a company in good standing." />
      ) : (
        <TableContainer>
          <Table size="sm">
            <Thead><Tr><Th {...tableHead}>Trip</Th><Th {...tableHead}>Company</Th><Th {...tableHead} isNumeric>Passengers</Th><Th {...tableHead} isNumeric>Refund</Th><Th {...tableHead} /></Tr></Thead>
            <Tbody>
              {trips.map(t => (
                <Tr key={t.tripId} _hover={{ bg: 'gray.50' }}>
                  <Td borderColor="gray.100"><Text fontWeight="medium" fontSize="sm">{t.route}</Text><Text fontSize="xs" color="gray.500">{formatDate(t.departure, true)} · {t.vehicle}</Text></Td>
                  <Td borderColor="gray.100"><Text fontSize="sm">{t.operatorName}</Text><Pill tone={t.operatorStatus === 'suspended' ? 'red' : 'orange'} dot={false}>{t.operatorStatus.replace('_', ' ')}</Pill></Td>
                  <Td borderColor="gray.100" isNumeric>{t.passengers}</Td>
                  <Td borderColor="gray.100" isNumeric>{t.refund ? formatNumber(t.refund) : '—'}</Td>
                  <Td borderColor="gray.100" isNumeric>
                    <HStack justify="flex-end" spacing={2}>
                      <Button size="xs" variant="outline" leftIcon={<ArrowRightLeft size={13} />} onClick={() => setMoving(t)}>Move bus</Button>
                      <Button size="xs" variant="ghost" colorScheme="red" leftIcon={<Ban size={13} />} onClick={() => setCancelling(t)}>Cancel</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
      <MoveTripModal trip={moving} onClose={() => setMoving(null)} onDone={refresh} />
      <ConfirmModal
        isOpen={!!cancelling} onClose={() => setCancelling(null)} tone="red" loading={cancel.isLoading}
        title={cancelling === 'all' ? `Cancel ${trips.length} trips` : 'Cancel this trip'} confirmLabel="Cancel and refund"
        body={cancelling === 'all'
          ? <>{totalPax} passenger(s) will be refunded <b>TZS {formatNumber(totalRefund)}</b> to their Daladala wallet and notified.</>
          : cancelling && <>{cancelling.route} · {formatDate(cancelling.departure, true)}. {cancelling.passengers} passenger(s) will be refunded <b>TZS {formatNumber(cancelling.refund)}</b> to their Daladala wallet and notified.</>}
        reasonLabel="Reason (included in the cancellation)" reasonRequired
        onConfirm={({ reason }) => cancel.mutate({ list: cancelling === 'all' ? trips : cancelling ? [cancelling] : [], reason })}
      />
    </Panel>
  )
}

// ---------------------------------------------------------------------------
export default function ControlCenter() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { user } = useAuthStore()
  const home = useQuery('control-home', () => control.home().then(r => r.data), { refetchInterval: 30_000 })
  const trips = useQuery('control-trips', () => control.atRisk().then(r => r.data), { refetchInterval: 60_000 })

  useEffect(() => {
    if (params.get('tab') === 'trips' && trips.data) document.getElementById('trips')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [params, trips.data])

  const d = home.data
  const needs = d?.attention.filter(a => a.tone !== 'info') ?? []
  const extra = d?.attention.filter(a => a.tone === 'info') ?? []
  const first = user?.firstName ? `, ${user.firstName}` : ''
  const subtitle = !d ? 'Loading what needs your attention…'
    : d.allClear ? 'Everything is in order. Nothing is waiting for you.'
      : `${needs.length} thing${needs.length === 1 ? '' : 's'} need${needs.length === 1 ? 's' : ''} you${extra.length ? `, plus ${extra.length} small one${extra.length === 1 ? '' : 's'}` : ''}.`

  return (
    <>
      <PageHeader
        eyebrow="Control center" title={`${greeting()}${first}`} subtitle={subtitle}
        actions={<Button variant="outline" size="sm" leftIcon={<Settings2 size={14} />} onClick={() => navigate('/inbox?tab=settings')}>Notification settings</Button>}
      />

      {home.isError && <Alert status="error" borderRadius="xl" mb={5}><AlertIcon /><AlertDescription>Could not load the control center. {apiErrorMessage(home.error)}</AlertDescription></Alert>}

      <VStack align="stretch" spacing={3} mb={8}>
        {home.isLoading ? <SkeletonRows rows={3} h={20} /> : d?.allClear && !extra.length ? (
          <Flex bg="white" border="1px solid" borderColor="brand.100" borderRadius="2xl" p={8} direction="column" align="center" textAlign="center">
            <Center w={14} h={14} borderRadius="full" bg="brand.50" color="brand.600"><CheckCircle2 size={28} /></Center>
            <Text mt={4} fontWeight="semibold" fontSize="lg" color="gray.900">You are all caught up</Text>
            <Text color="gray.500" fontSize="sm" maxW="md" mt={1}>New companies, buses, documents and withdrawals will show up here the moment they need a decision.</Text>
          </Flex>
        ) : (
          <>
            {needs.map(a => <AttentionCard key={a.id} item={a} />)}
            {extra.length > 0 && (
              <>
                {needs.length > 0 && <Text fontSize="xs" fontWeight="semibold" letterSpacing="0.06em" textTransform="uppercase" color="gray.400" pt={2}>When you have a moment</Text>}
                {extra.map(a => <AttentionCard key={a.id} item={a} />)}
              </>
            )}
          </>
        )}
      </VStack>

      <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={4} mb={8}>
        <StatTile label="Active companies" icon={<Building2 size={20} />} value={d?.kpis.activeCompanies ?? 0} loading={!d} onClick={() => navigate('/operators')} />
        <StatTile label="Buses on the road" icon={<Truck size={20} />} tone="blue" value={d?.kpis.liveBuses ?? 0} loading={!d} />
        <StatTile label="Partner rides, 7 days" icon={<Users size={20} />} tone="purple" value={formatNumber(d?.kpis.ridesLast7d ?? 0)} loading={!d} />
        <StatTile label="Commission this month" icon={<Percent size={20} />} value={d ? <Money value={d.kpis.commissionThisMonth} size="md" /> : 0} loading={!d} onClick={() => navigate('/commission')} />
        <StatTile label="We owe companies" icon={<Wallet size={20} />} tone="orange" value={d ? <Money value={d.kpis.owedToCompanies} size="md" /> : 0} loading={!d} onClick={() => navigate('/payouts')} />
        <StatTile label="Paid out this month" icon={<Banknote size={20} />} tone="gray" value={d ? <Money value={d.kpis.paidOutThisMonth} size="md" /> : 0} loading={!d} />
      </SimpleGrid>

      {((trips.data?.length ?? 0) > 0 || trips.isLoading) && <Box mb={8}><TripsAtRisk trips={trips.data ?? []} loading={trips.isLoading} /></Box>}

      <Panel
        title="Recent activity" subtitle="Everything staff and companies did, newest first" flush
        actions={d?.lastMoneyCheck ? (
          <HStack spacing={2}><Text fontSize="xs" color="gray.500">Money check {timeAgo(d.lastMoneyCheck.at)}</Text>
            <Pill tone={d.lastMoneyCheck.status === 'clean' ? 'brand' : 'orange'}>{d.lastMoneyCheck.status === 'clean' ? 'All matched' : 'Needs a look'}</Pill></HStack>
        ) : undefined}
      >
        {home.isLoading ? <SkeletonRows rows={4} h={8} /> : !d?.activity.length ? <EmptyState icon={<Clock size={26} />} title="No activity yet" /> : (
          <VStack align="stretch" spacing={0} divider={<Box h="1px" bg="gray.100" />}>
            {d.activity.map(a => (
              <Flex key={a.id} px={5} py={3} justify="space-between" gap={4} _hover={{ bg: 'gray.50' }} cursor={a.operatorId ? 'pointer' : 'default'}
                onClick={() => a.operatorId && navigate(`/operators?open=${a.operatorId}`)}>
                <Text fontSize="sm" color="gray.700" minW={0} noOfLines={2}>
                  <Text as="span" fontWeight="medium" color="gray.900">{a.actorRole === 'admin' ? 'Staff ' : a.actorRole === 'operator' ? 'A company ' : ''}</Text>
                  {ACTIVITY_LABEL[a.action] ?? a.action.replace(/[._]/g, ' ')}
                  {a.summary ? <Text as="span" color="gray.500"> · {a.summary}</Text> : null}
                </Text>
                <Text fontSize="xs" color="gray.400" flexShrink={0}>{timeAgo(a.at)}</Text>
              </Flex>
            ))}
          </VStack>
        )}
      </Panel>
    </>
  )
}
